import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Navigate } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  MenuItem,
  InputAdornment,
  CircularProgress,
  useTheme,
} from '@mui/material';
import Search from '@mui/icons-material/Search';
import Download from '@mui/icons-material/Download';
import Groups from '@mui/icons-material/Groups';
import { useSnackbar } from 'notistack';
import { fetchAlumni } from '../../store/slices/alumniSlice';
import * as alumniApi from '../../api/alumniApi';
import { FACULTY_OPTIONS } from '../../utils/formationConstants';
import { getCountries } from '../../utils/countries';

export default function AlumniData() {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const { user } = useSelector((s) => s.auth);
  const { list: alumni, loading } = useSelector((s) => s.alumni);
  const [countries, setCountries] = useState([]);
  const [filters, setFilters] = useState({ country: '', faculty: '', search: '' });

  useEffect(() => {
    dispatch(fetchAlumni(filters));
  }, [dispatch, filters.country, filters.faculty, filters.search]);

  useEffect(() => {
    getCountries().then(setCountries).catch(() => setCountries([]));
  }, []);

  if (user?.role !== 'admin' && user?.role !== 'moderator') return <Navigate to="/dashboard" replace />;

  const handleExport = async () => {
    try {
      const blob = await alumniApi.exportAlumni(filters);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'alumni.xlsx';
      a.click();
      URL.revokeObjectURL(url);
      enqueueSnackbar('Export réussi.', { variant: 'success' });
    } catch (err) {
      enqueueSnackbar(err.response?.data?.message || 'Erreur lors de l\'export.', { variant: 'error' });
    }
  };

  return (
    <Box sx={{ width: '100%', overflow: 'hidden' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
        <Groups sx={{ color: 'primary.main', fontSize: 32 }} />
        <Box>
          <Typography variant="h5" fontWeight="bold">
            Données Alumni
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {alumni.length} inscription{alumni.length !== 1 ? 's' : ''}
          </Typography>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
        <TextField
          size="small"
          placeholder="Rechercher par nom, e-mail…"
          value={filters.search}
          onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" />
              </InputAdornment>
            ),
          }}
          sx={{ minWidth: 220, flex: 1 }}
        />
        <TextField
          select
          size="small"
          label="Pays"
          value={filters.country}
          onChange={(e) => setFilters((f) => ({ ...f, country: e.target.value }))}
          sx={{ minWidth: 180 }}
        >
          <MenuItem value="">Tous les pays</MenuItem>
          {countries.map((c) => (
            <MenuItem key={c.code} value={c.name}>
              {c.name}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          size="small"
          label="Faculté"
          value={filters.faculty}
          onChange={(e) => setFilters((f) => ({ ...f, faculty: e.target.value }))}
          sx={{ minWidth: 200 }}
        >
          <MenuItem value="">Toutes les facultés</MenuItem>
          {FACULTY_OPTIONS.map((f) => (
            <MenuItem key={f.value} value={f.value}>
              {f.label}
            </MenuItem>
          ))}
        </TextField>
        <Button variant="outlined" startIcon={<Download />} onClick={handleExport} disabled={loading}>
          Télécharger Excel
        </Button>
      </Box>

      <Paper
        elevation={0}
        sx={{
          borderRadius: 2,
          border: `1px solid ${theme.palette.divider}`,
          overflow: 'hidden',
        }}
      >
        <TableContainer sx={{ maxHeight: 520 }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Nom</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>E-mail</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Pays</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Année diplôme</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Faculté</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Sous-domaine</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <CircularProgress size={32} />
                  </TableCell>
                </TableRow>
              ) : alumni.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                    Aucune donnée alumni.
                  </TableCell>
                </TableRow>
              ) : (
                alumni.map((a) => (
                  <TableRow key={a._id} hover>
                    <TableCell>{a.fullname}</TableCell>
                    <TableCell>{a.email}</TableCell>
                    <TableCell>{a.country}</TableCell>
                    <TableCell>{a.graduationYear || '—'}</TableCell>
                    <TableCell>{a.faculty ? (FACULTY_OPTIONS.find((f) => f.value === a.faculty)?.label || a.faculty) : '—'}</TableCell>
                    <TableCell>{a.subdomain || '—'}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
}
